/**
 * THE FILE — the controls.
 *
 * A small set on purpose. This product asks very few questions, and the ones it
 * asks are a sentence, a date, a choice from records already in hand, or a file.
 * Anything more elaborate would be a form, and the whole direction is that the
 * engine proposes and the accountant confirms rather than filling a form in to
 * earn the right to start.
 *
 * VON RESTORFF, ENFORCED: exactly ONE saturated element per screen, and it is
 * the primary action. That is why there is no filled secondary, no tinted
 * variant and no accent-coloured link here — each of them was a candidate and
 * each would have spent the one slot the eye has.
 */
import { clsx } from "clsx";
import { useRef } from "react";
import type { ComponentPropsWithoutRef, ReactNode } from "react";

const BASE =
  "inline-flex items-center justify-center gap-1.5 whitespace-nowrap rounded-control font-medium transition-colors duration-150 disabled:cursor-not-allowed";

const SIZE = {
  md: "h-[34px] px-4 text-sm",
  sm: "h-8 px-3 text-sm",
} as const;

const VARIANT = {
  /** The one saturated element. There is at most one of these on a screen. */
  primary: "bg-forest text-forest-on hover:bg-forest-hover disabled:bg-brand-line",
  /** An outline, so it reads as available without competing with the primary. */
  secondary: "border border-line-strong bg-transparent text-ink hover:bg-surface disabled:text-stone",
  /** Words. For the actions that are genuinely asides — Change, Cancel, Back. */
  ghost: "px-1.5 text-ink hover:underline disabled:text-stone",
} as const;

export interface ButtonProps extends Omit<ComponentPropsWithoutRef<"button">, "className"> {
  variant?: keyof typeof VARIANT | undefined;
  size?: keyof typeof SIZE | undefined;
  className?: string | undefined;
}

/**
 * `type="button"` by default. A button inside a form defaults to submit in
 * HTML, which is how a "Back" or a "Change" ends up posting the form it was
 * standing beside — a real defect found in this estate, not a hypothetical.
 * A caller that means submit says so.
 *
 * whitespace-nowrap because a two-word label wrapping mid-phrase inside a
 * 34px control looks like a rendering fault rather than a button.
 */
export function Button({ variant = "primary", size = "md", className, type, ...rest }: ButtonProps) {
  return <button type={type ?? "button"} className={clsx(BASE, SIZE[size], VARIANT[variant], className)} {...rest} />;
}

export interface FieldProps {
  /** Omit it and the field is bare — for a control whose context already names it. */
  label?: ReactNode;
  /** One quiet line under the control. Never a tooltip: a hint nobody can see is not a hint. */
  hint?: ReactNode;
  children: ReactNode;
  className?: string | undefined;
}

/**
 * Label above, control, hint under — and the whole thing IS the `<label>`, so
 * there is no id to plumb and therefore no way for a label to become detached
 * from the control it names. Label-beside-field is never used: it costs a
 * column, and single-column forms complete faster with fewer errors.
 */
export function Field({ label, hint, children, className }: FieldProps) {
  return (
    <label className={clsx("block", className)}>
      {label && <div className="mb-1.5 text-sm font-medium text-ink-soft">{label}</div>}
      {children}
      {hint && <div className="mt-1 text-[13px] text-stone">{hint}</div>}
    </label>
  );
}

/**
 * The control chrome, shared by the text field and the select so the two never
 * drift apart by a pixel when they sit next to each other.
 *
 * Note what is NOT here: `focus:outline-none`. The brand ring declared in the
 * sheet is the one focus indicator in the product, and a control that removes
 * it and substitutes a border colour has replaced an accessibility guarantee
 * with a hint. The border change is in addition to the ring, not instead of it.
 */
const CONTROL = "h-[34px] w-full rounded-control border border-line-strong bg-canvas text-sm text-ink focus:border-brand";

export interface TextFieldProps extends Omit<ComponentPropsWithoutRef<"input">, "className"> {
  label?: ReactNode;
  hint?: ReactNode;
  className?: string | undefined;
}

/** A single line of an answer. */
export function TextField({ label, hint, className, ...rest }: TextFieldProps) {
  return (
    <Field label={label} hint={hint}>
      <input className={clsx(CONTROL, "px-3 placeholder:text-stone", className)} {...rest} />
    </Field>
  );
}

export interface SelectProps extends Omit<ComponentPropsWithoutRef<"select">, "className"> {
  label?: ReactNode;
  hint?: ReactNode;
  className?: string | undefined;
  children: ReactNode;
}

/**
 * A native select, deliberately. A hand-rolled dropdown loses keyboard
 * type-ahead, loses the platform's own touch picker, and has to reinvent focus
 * trapping to get back to where the browser started — and the choices this
 * product offers are short lists the native control handles perfectly.
 */
export function Select({ label, hint, className, children, ...rest }: SelectProps) {
  return (
    <Field label={label} hint={hint}>
      <select className={clsx(CONTROL, "px-2.5", className)} {...rest}>
        {children}
      </select>
    </Field>
  );
}

export interface FileButtonProps {
  children: ReactNode;
  /** The families the reader accepts, e.g. ".xlsx,.xls,.csv". The server decides admission. */
  accept?: string | undefined;
  multiple?: boolean | undefined;
  disabled?: boolean | undefined;
  variant?: keyof typeof VARIANT | undefined;
  size?: keyof typeof SIZE | undefined;
  className?: string | undefined;
  onFiles: (files: File[]) => void;
}

/**
 * Enclose records.
 *
 * A real `<button>` driving a hidden input, rather than a styled `<label>`
 * wrapping one: the button is what receives focus and therefore what shows the
 * focus ring, and a visually-hidden input that owns focus draws the ring around
 * nothing.
 *
 * The input's value is cleared after every selection. Without that, choosing
 * the SAME file twice in a row fires no change event at all — the browser
 * compares values, sees no difference, and stays silent. The accountant
 * re-picks the file they just fixed and the product does nothing, with no
 * error to explain why.
 */
export function FileButton({
  children,
  accept,
  multiple,
  disabled,
  variant = "secondary",
  size = "md",
  className,
  onFiles,
}: FileButtonProps) {
  const input = useRef<HTMLInputElement>(null);
  return (
    <>
      <Button variant={variant} size={size} disabled={disabled} className={className} onClick={() => input.current?.click()}>
        {children}
      </Button>
      <input
        ref={input}
        type="file"
        accept={accept}
        multiple={multiple}
        className="hidden"
        onChange={(event) => {
          const files = Array.from(event.target.files ?? []);
          event.target.value = "";
          if (files.length > 0) onFiles(files);
        }}
      />
    </>
  );
}
